/**
 * UserNavSimple Component
 *
 * Simple user navigation for users authenticated with Azure B2C
 * but without complete contact details in microservices.
 *
 * Uses JWT data only (email, name from Azure B2C session).
 */

"use client"

import { Button } from "@/components/ui/Button"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import UserAvatar from "@/components/ui/UserAvatar"
import { useLogout } from "@/hooks/useLogout"
import { ExternalLink, LogOut, UserPlus } from "lucide-react"
import * as React from "react"

interface UserNavSimpleProps {
  userName: string
  userEmail: string
}

export default function UserNavSimple({
  userName,
  userEmail,
}: UserNavSimpleProps) {
  const { logout } = useLogout()
  const [open, setOpen] = React.useState(false)

  const handleLogout = () => {
    setOpen(false)
    logout()
  }

  const handleCompleteRegistration = () => {
    const connectUrl = new URL(
      `${process.env.NEXT_PUBLIC_ACCOUNT_APP_URL || "https://connect.uic.org"}`
    )

    // Add app parameter
    connectUrl.searchParams.set(
      "app",
      process.env.NEXT_UIC_APP_NAME || "sustainability-hub"
    )

    // Add return URL using NEXT_PUBLIC_APP_URL with current path
    const appUrl = process.env.NEXT_PUBLIC_APP_URL || window.location.origin
    const returnUrl = `${appUrl}${window.location.pathname}${window.location.search}`
    connectUrl.searchParams.set("return_url", returnUrl)

    window.location.href = connectUrl.toString()
  }

  // Extract first name from full name for display
  const displayName = userName

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="rounded-full ml-2 h-auto p-2 hover:bg-accent"
          aria-label={`User menu for ${displayName || userEmail}`}
        >
          <UserAvatar userName={displayName} photoUrl={null} size="sm" />
          <div className="flex-1 text-left text-sm leading-tight ml-2 hidden md:block">
            <span className="truncate font-medium max-w-[190px] block">
              {displayName}
            </span>
            <span className="text-xs text-muted-foreground truncate max-w-[190px] block">
              {userEmail}
            </span>
          </div>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{displayName}</p>
            <p className="text-xs leading-none text-muted-foreground">
              {userEmail}
            </p>
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={handleCompleteRegistration}
          className="cursor-pointer"
        >
          <UserPlus className="mr-2 h-4 w-4" />
          <span>Complete my registration</span>
          <ExternalLink className="ml-auto h-3 w-3 opacity-50" />
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={handleLogout}
          className="cursor-pointer text-red-600 focus:text-red-600"
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span>Logout</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
